<template>
    <div class="presets-content">
        <div v-if="!groupedPresets || groupedPresets.length === 0" class="no-results">
            <font-awesome-icon :icon="['fa-solid', 'fa-search']" class="no-results-icon" />
            <h4>No effects found</h4>
            <p>Try adjusting your search terms or filters</p>
        </div>
        <template v-else>
            <div class="grouped-presets">
                <div v-for="group in groupedPresets" :key="group.categoryInfo.category" class="category-group">
                    <div class="category-header d-flex">
                        <div v-if="group.categoryInfo.icon">
                            <font-awesome-icon :icon="['fa-solid', group.categoryInfo.icon]" class="mr-2" />
                        </div>
                        <div>
                            <h3 class="category-title">{{ group.categoryInfo.label }}</h3>
                            <p class="category-description">{{ group.categoryInfo.description }}</p>
                        </div>
                    </div>
                    <div class="presets-grid">
                        <body-preset-grid-thumbnail
                            v-for="preset in group.presets"
                            :key="preset.id"
                            :preset="preset"
                            :is-selected="selectedPreset?.id === preset.id"
                            @select="handlePresetSelect"
                        />
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import store from '@/store';
import { ImageEffectPreset } from '@/components/DesignStudio/ImageEffectsProperties/types/presets';
import { createEffectFromPreset } from '@/components/DesignStudio/ImageEffectsProperties/utils/effectCreators';
import BodyPresetGridThumbnail from '@/components/DesignStudio/ImageEffectsProperties/components/create/components/body/preset/grid/thumbnail/BodyPresetGridThumbnail.vue';

const selectedPreset = computed(() => store.getters['imageEffectsProperties/selectedPreset']);
const groupedPresets = computed(() => store.getters['imageEffectsProperties/groupedFilteredPresets']);

const handlePresetSelect = (preset: ImageEffectPreset) => {
    const effect = createEffectFromPreset(preset);
    store.dispatch('imageEffectsProperties/addImageEffect', effect);
};
</script>

<style scoped lang="scss">
.presets-content {
    margin-bottom: 24px;
}

.no-results {
    text-align: center;
    padding: 48px 24px;
    color: #6c757d;

    .no-results-icon {
        font-size: 48px;
        margin-bottom: 16px;
        color: #dee2e6;
    }

    h4 {
        margin: 0 0 8px;
        font-size: 18px;
    }

    p {
        margin: 0;
        font-size: 14px;
    }
}

.presets-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 16px;
    max-height: 400px;
    overflow-y: auto;
}

.grouped-presets {
    overflow-y: auto;
    .presets-grid {
        max-height: none;
        overflow-y: visible;
        margin-bottom: 0;
    }
}

.category-group {
    margin-bottom: 12px;
    &:last-child {
        margin-bottom: 0;
    }
}

.category-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
}

.category-header {
    margin: 16px 0 6px 0;
}

.presets-grid::-webkit-scrollbar {
    width: 6px;
}

.presets-grid::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.presets-grid::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
        background: #a8a8a8;
    }
}

@media (max-width: 768px) {
    .presets-grid {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 8px;
    }
}
</style>
